import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useVisibilityObserver } from '@noths/polaris-client-react-hooks';
import { grid } from '@noths/polaris-client-ribbons-base';

import { Accordion } from 'src/components/molecules/Accordion';
import { DeliveryBlockContainer } from 'src/components/molecules/DeliveryBlock';
import { sectionVisible } from 'src/redux/visibilityTracking';
import { InventoryStatus, Product } from 'src/types';
import { isOutOfStock } from 'src/utils';
import * as copy from '../constants/copy';
import { DELIVERY_AREA_VISIBILITY_NAME } from '../tracking/events';
import * as styles from '../styles/DeliveryArea.styles';

export interface DeliveryAreaProps {
  inventoryStatus: InventoryStatus;
  product: Product;
}

export interface DispatchProps {
  onDeliveryAreaToggle: (isOpen: boolean) => void;
  onDeliveryAreaReturnsPolicyClick: () => void;
}

export const DELIVERY_AREA_ID_PREFIX = 'delivery-area-';
const DELIVERY_AREA_VISIBILITY_THRESHOLD = 1;

export const DeliveryArea = ({
  inventoryStatus,
  product,
  onDeliveryAreaToggle,
  onDeliveryAreaReturnsPolicyClick,
}: DeliveryAreaProps & DispatchProps) => {
  const dispatch = useDispatch();
  const { observedElementRef: deliveryAreaRef } = useVisibilityObserver(
    () => dispatch(sectionVisible({ sectionName: DELIVERY_AREA_VISIBILITY_NAME })),
    { observerOptions: { threshold: DELIVERY_AREA_VISIBILITY_THRESHOLD } },
  );

  const returnsInformationRef = useRef<HTMLDivElement | null>(null);

  useEffect((): void | VoidFunction => {
    const returnsInfoLink = returnsInformationRef.current?.querySelector('a');

    returnsInfoLink?.addEventListener('click', onDeliveryAreaReturnsPolicyClick);

    return () => {
      returnsInfoLink?.removeEventListener('click', onDeliveryAreaReturnsPolicyClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Accordion
      ref={deliveryAreaRef}
      title={copy.DELIVERY_AREA}
      id={styles.DELIVERY_SUMMARY_ID}
      styles={styles.deliveryArea}
      onToggleOpen={onDeliveryAreaToggle}
    >
      <div css={grid}>
        {!isOutOfStock(inventoryStatus) && (
          <div css={styles.deliveryAreaSection}>
            <DeliveryBlockContainer idPrefix={DELIVERY_AREA_ID_PREFIX} />
          </div>
        )}
        <div css={styles.deliveryAreaSection}>
          {product.shippingInfo && (
            <>
              <h3 css={styles.heading}>{copy.DELIVERY_INFO}</h3>
              <div dangerouslySetInnerHTML={{ __html: product.shippingInfo }} />
            </>
          )}
          {product.returnsInformation && (
            <>
              <h3 css={styles.heading}>{copy.RETURNS}</h3>
              <div
                ref={returnsInformationRef}
                dangerouslySetInnerHTML={{ __html: product.returnsInformation }}
              />
            </>
          )}
        </div>
      </div>
    </Accordion>
  );
};
